import { useTranslation } from "react-i18next";
import { useThemeStore, type Theme } from "../../store/theme.store";
import { PillMenu } from "./PillMenu";

// Each theme's page, card and accent (from themes.css), so the menu previews
// a theme without switching to it.
const THEMES: { value: Theme; key: string; fallback: string; bg: string; card: string; accent: string; paint?: string }[] = [
  { value: "teal", key: "teal", fallback: "Default", bg: "232 241 242", card: "245 250 250", accent: "41 130 137" },
  { value: "light", key: "light", fallback: "Light", bg: "248 250 252", card: "255 255 255", accent: "86 103 158" },
  { value: "dark", key: "dark", fallback: "Dark", bg: "13 18 32", card: "21 28 44", accent: "82 102 154" },
  { value: "custom", key: "vibrant", fallback: "Vibrant", bg: "36 42 45", card: "46 54 58", accent: "220 247 99" },
  { value: "mono", key: "mono", fallback: "Black&White", bg: "245 245 245", card: "255 255 255", accent: "23 23 23" },
  {
    value: "glass", key: "glass", fallback: "Glass", bg: "236 240 250", card: "255 255 255 / 0.6", accent: "99 82 220",
    paint: "linear-gradient(135deg, rgb(186 205 255), rgb(225 200 250) 50%, rgb(180 236 228))",
  },
];

const Swatch = ({ bg, card, accent, paint }: { bg: string; card: string; accent: string; paint?: string }) => (
  <span
    aria-hidden
    className="relative block h-5 w-5 overflow-hidden rounded-md border border-[rgb(var(--border))]"
    style={{ background: paint ?? `rgb(${bg})` }}
  >
    <span className="absolute inset-x-0 bottom-0 h-1/2" style={{ background: `rgb(${card})` }} />
    <span
      className="absolute bottom-[3px] right-[3px] h-2 w-2 rounded-full ring-1 ring-black/10"
      style={{ background: `rgb(${accent})` }}
    />
  </span>
);

export default function ThemeSwitcher() {
  const { t } = useTranslation("common");
  const { theme, setTheme } = useThemeStore();

  return (
    <PillMenu
      value={theme}
      onChange={(v) => setTheme(v as Theme)}
      label={t("theme", { defaultValue: "Theme" })}
      options={THEMES.map((th) => ({
        value: th.value,
        label: t(th.key, { defaultValue: th.fallback }),
        lead: <Swatch bg={th.bg} card={th.card} accent={th.accent} paint={th.paint} />,
      }))}
    />
  );
}
